import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { graphqlRequest } from './graphql'
import { useAuth } from '../auth'
import type {
  Order as SharedOrder,
  OrderItem as SharedOrderItem,
  OrderItemInput,
  PaymentSelectionInput,
  PaymentStatus,
  PaymentMethod,
  PaymentProvider,
} from '@shinkansen/graphql'

// Re-export types from shared package for convenience
export type { PaymentStatus, PaymentMethod, PaymentProvider, PaymentSelectionInput, OrderItemInput }

/**
 * Order item interface - re-exported from shared package
 */
export type OrderItem = SharedOrderItem

/**
 * Order interface - using shared type with additional runtime fields
 * Note: The shared Order type matches the GraphQL schema exactly
 */
export type Order = SharedOrder

/**
 * Input for creating an order (client-side interface)
 * Note: payerEmail is passed separately to the GraphQL mutation
 */
export interface CreateOrderInput {
  items: OrderItemInput[]
  deliveryAddress: string
  deliveryLat: number
  deliveryLng: number
  notes?: string
  /** Delivery fee in smallest currency unit (e.g., IDR) */
  deliveryFee?: number
  paymentSelection?: PaymentSelectionInput
  payerEmail: string
}

const ORDER_FIELDS = `
  id
  externalId
  customerId
  status
  totalAmount
  deliveryFee
  currency
  deliveryAddress
  notes
  assignedDriverId
  estimatedDelivery
  paymentStatus
  paymentUrl
  paymentMethod
  paymentChannel
  paymentProvider
  paymentProviderReference
  paymentExpiresAt
  paymentTransactionId
  paidAt
  createdAt
  updatedAt
`

const ORDER_DETAIL_FIELDS = `
  ${ORDER_FIELDS}
  paymentInstructions
  cogsAllocation {
    fulfillmentMode
    allocationSource
    reportingState
    stockMovementCount
    allocatedAt
  }
`

export function useMyOrders() {
  const { isAuthenticated, isLoading } = useAuth()
  
  return useQuery({
    queryKey: ['myOrders'],
    queryFn: async () => {
      const result = await graphqlRequest<{ myOrders: Order[] }>(`
        query MyOrders {
          myOrders {
            ${ORDER_FIELDS}
          }
        }
      `)
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.myOrders ?? []
    },
    // Only fetch when authenticated and session is loaded
    enabled: isAuthenticated && !isLoading,
  })
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: ['order', id],
    queryFn: async () => {
      const result = await graphqlRequest<{ order: Order | null }>(`
        query Order($id: UUID!) {
          order(id: $id) {
            ${ORDER_DETAIL_FIELDS}
          }
        }
      `, { id })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.order
    },
    enabled: !!id,
  })
}

export function useCreateOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateOrderInput) => {
      const { payerEmail, deliveryFee, ...rest } = input
      const orderInput = {
        ...rest,
        deliveryFee: deliveryFee ?? 0,
      }
      const result = await graphqlRequest<{ createOrder: Order }>(`
        mutation CreateOrder($input: CreateOrderInput!, $payerEmail: String!) {
          createOrder(input: $input, payerEmail: $payerEmail) {
            ${ORDER_FIELDS}
          }
        }
      `, { input: orderInput, payerEmail })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.createOrder
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myOrders'] })
    },
  })
}

export function useVerifyPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orderId: string) => {
      const result = await graphqlRequest<{ verifyPayment: Order | null }>(`
        query VerifyPayment($orderId: UUID!) {
          verifyPayment(orderId: $orderId) {
            ${ORDER_DETAIL_FIELDS}
          }
        }
      `, { orderId })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.verifyPayment
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['order', data.id], data)
        queryClient.invalidateQueries({ queryKey: ['myOrders'] })
      }
    },
  })
}

export function useCancelOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orderId: string) => {
      const result = await graphqlRequest<{ cancelOrder: Order }>(`
        mutation CancelOrder($orderId: UUID!) {
          cancelOrder(orderId: $orderId) {
            ${ORDER_FIELDS}
          }
        }
      `, { orderId })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.cancelOrder
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myOrders'] })
    },
  })
}
